/* eslint-disable react-refresh/only-export-components */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode
} from "react";

import type { CaptionDisplay } from "@/audio/captions";
import type {
  ActivityCompletionMetrics,
  ActivityExecutionContext
} from "@/app/integration/contracts";
import { LEVEL_REGISTRY } from "@/content/registry";
import type { EnglishActivityType } from "@/content/english";
import type { LevelDefinition } from "@/domain";
import {
  ActivityHost,
  type ActivityHostBridgeEvents,
  type ActivityHostRenderContext
} from "@/features/activity-host";
import {
  type CurriculumAudioPlayer,
  useAudio,
  useCurriculumAudioPlayer
} from "@/features/audio";
import {
  ChineseLevelPlayer,
  getChinesePlayerLevel,
  type ChineseNarrationRequest
} from "@/features/domain-players/chinese";
import {
  getEnglishPlayerBinding,
  type EnglishActivityHostBridge,
  type EnglishActivityHostBridgeEvents,
  type EnglishActivityNarrationPlayCommand,
  type EnglishLetterSoundPlayerBinding,
  type EnglishPictureWordPlayerBinding,
  type EnglishPlayerBindingBase,
  type EnglishRhythmPlayerBinding
} from "@/features/domain-players/english";
import {
  FocusSocialFollowDirectionsRenderer,
  FocusSocialFriendshipStoryRenderer,
  FocusSocialTargetSearchRenderer,
  focusSocialFollowDirectionsActions,
  focusSocialFollowDirectionsAdapter,
  focusSocialFriendshipStoryActions,
  focusSocialFriendshipStoryAdapter,
  focusSocialTargetSearchActions,
  focusSocialTargetSearchAdapter,
  getFocusSocialPlayerBinding
} from "@/features/domain-players/focus-social";
import {
  HabitsLevelPlayer,
  getHabitsLevelBinding,
  type HabitsAudioReplayRequest
} from "@/features/domain-players/habits";
import {
  MathLevelPlayer,
  getMathPlayerBinding
} from "@/features/domain-players/math";
import {
  definePlayRenderer,
  type PlayRendererHostProps,
  type PlayRendererRegistration,
  type PlayRendererRegistry
} from "@/features/play";
import {
  createEventBridge,
  type EventBridge
} from "@/game/core/event-bridge";

import { StandaloneActivityGate } from "./StandaloneActivityGate";

export const APP_PLAY_ACTIVITY_TYPES = [
  "sound-bubbles",
  "picture-character-match",
  "story-sequence",
  "number-train",
  "forest-market",
  "pattern-bridge",
  "letter-sound",
  "picture-word-match",
  "rhythm-phrases",
  "daily-routine",
  "pack-schoolbag",
  "school-scenarios",
  "target-search",
  "follow-directions",
  "friendship-stories"
] as const;

export type AppPlayActivityType =
  (typeof APP_PLAY_ACTIVITY_TYPES)[number];

export interface AppPlayerMatrixEntry {
  readonly activityType: AppPlayActivityType;
  readonly domain: LevelDefinition["domain"];
  readonly levelId: string;
}

function useStandaloneCompletion(
  {
    level,
    nowMs,
    onComplete,
    sessionKey
  }: PlayRendererHostProps,
  activityType: string
): {
  readonly complete: (metrics: ActivityCompletionMetrics) => void;
  readonly start: () => void;
} {
  const now = nowMs ?? Date.now;
  const startedAtRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    startedAtRef.current = null;
  }, [sessionKey]);

  const start = useCallback(() => {
    startedAtRef.current ??= now();
  }, [now]);

  const complete = useCallback(
    (metrics: ActivityCompletionMetrics) => {
      const completedAt = now();
      onComplete({
        ...metrics,
        activityType,
        durationMs: Math.max(
          0,
          completedAt - (startedAtRef.current ?? completedAt)
        ),
        levelId: level.id
      });
    },
    [activityType, level.id, now, onComplete]
  );

  return { complete, start };
}

function requireChinesePlayerLevel(level: LevelDefinition) {
  const playerLevel = getChinesePlayerLevel(level.id);
  if (
    playerLevel === undefined ||
    playerLevel.activityType !== level.activityType
  ) {
    throw new Error("Missing Chinese player binding for " + level.id);
  }
  return playerLevel;
}

function requireMathPlayerBinding(level: LevelDefinition) {
  const binding = getMathPlayerBinding(level.id);
  if (
    binding === undefined ||
    binding.activityType !== level.activityType
  ) {
    throw new Error("Missing Math player binding for " + level.id);
  }
  return binding;
}

function requireHabitsPlayerBinding(level: LevelDefinition) {
  const binding = getHabitsLevelBinding(level.id);
  if (
    binding === undefined ||
    binding.activityType !== level.activityType
  ) {
    throw new Error("Missing Habits player binding for " + level.id);
  }
  return binding;
}

function ChinesePlayRenderer(props: PlayRendererHostProps) {
  const playerLevel = requireChinesePlayerLevel(props.level);
  const audioPlayer = useCurriculumAudioPlayer(props.sessionKey);
  const { playPath } = audioPlayer;

  const handleNarrationRequest = useCallback(
    (request: ChineseNarrationRequest) => {
      if (request.kind !== "instruction") {
        void playPath(request.audioPath);
      }
    },
    [playPath]
  );

  return (
    <ChineseLevelPlayer
      audioPlayer={audioPlayer}
      key={props.sessionKey}
      levelId={playerLevel.id}
      nowMs={props.nowMs}
      onComplete={props.onComplete}
      onExit={props.onExit}
      onHostCommand={props.onCommand}
      onHostReady={props.onReady}
      onLevelError={props.onLevelError}
      onNarrationRequest={handleNarrationRequest}
      reducedMotion={props.reducedMotion}
    />
  );
}

function MathPlayRenderer(props: PlayRendererHostProps) {
  const binding = requireMathPlayerBinding(props.level);
  const audioPlayer = useCurriculumAudioPlayer(props.sessionKey);

  const context = useMemo<ActivityExecutionContext>(
    () => ({
      nowMs: props.nowMs ?? Date.now,
      reducedMotion: props.reducedMotion ?? false
    }),
    [props.nowMs, props.reducedMotion]
  );
  const { complete: handleComplete, start: handleStart } =
    useStandaloneCompletion(props, binding.activityType);

  return (
    <StandaloneActivityGate
      instruction={binding.level.instruction}
      levelTitle={binding.level.title}
      onActivityStart={handleStart}
      onCommand={props.onCommand}
      onReady={props.onReady}
      sessionKey={props.sessionKey}
    >
      <MathLevelPlayer
        audioPlayer={audioPlayer}
        executionContext={context}
        key={props.sessionKey}
        level={binding.level}
        onComplete={handleComplete}
      />
    </StandaloneActivityGate>
  );
}

function HabitsPlayRenderer(props: PlayRendererHostProps) {
  const binding = requireHabitsPlayerBinding(props.level);
  const audioPlayer = useCurriculumAudioPlayer(props.sessionKey);
  const { playPath } = audioPlayer;

  const { complete: handleComplete, start: handleStart } =
    useStandaloneCompletion(props, binding.activityType);
  const handleAudioReplay = useCallback(
    (request: HabitsAudioReplayRequest) => {
      void playPath(request.audioPath);
    },
    [playPath]
  );

  return (
    <StandaloneActivityGate
      instruction={binding.level.instruction}
      levelTitle={binding.level.title}
      onActivityStart={handleStart}
      onCommand={props.onCommand}
      onReady={props.onReady}
      sessionKey={props.sessionKey}
    >
      <HabitsLevelPlayer
        audioPlayer={audioPlayer}
        key={props.sessionKey}
        levelId={binding.level.id}
        onAudioReplay={handleAudioReplay}
        onComplete={handleComplete}
        reducedMotion={props.reducedMotion}
      />
    </StandaloneActivityGate>
  );
}

interface EnglishAudioAdapterProps<TAction> {
  readonly bridge: EnglishActivityHostBridge<TAction>;
}

interface ActiveEnglishNarration<TAction> {
  readonly command: EnglishActivityNarrationPlayCommand<TAction>;
  started: boolean;
}

function fallbackCaption<TAction>(
  command: EnglishActivityNarrationPlayCommand<TAction>
): CaptionDisplay {
  return {
    endMs: null,
    mode: "fallback",
    startMs: null,
    text: command.caption,
    trackId: command.audioKey
  };
}

function EnglishActivityAudioAdapter<TAction>({
  bridge
}: EnglishAudioAdapterProps<TAction>): null {
  const { commands, snapshot } = useAudio();
  const activeRef = useRef<ActiveEnglishNarration<TAction> | null>(null);

  useEffect(() => {
    const unsubscribe = bridge.subscribe("narration", (command) => {
      if (command.type === "stop-activity-narration") {
        const active = activeRef.current;
        if (
          active !== null &&
          active.command.audioKey === command.audioKey &&
          active.command.playbackRequest === command.playbackRequest
        ) {
          activeRef.current = null;
          commands.stopVoice();
        }
        return;
      }

      const active: ActiveEnglishNarration<TAction> = {
        command,
        started: false
      };
      activeRef.current = active;
      bridge.emit("caption", fallbackCaption(command));

      void commands
        .unlock()
        .then((unlock) =>
          unlock.status === "unlocked"
            ? commands.playVoice(command.audioKey)
            : null
        )
        .then((result) => {
          if (activeRef.current !== active) {
            return;
          }

          if (result?.status === "playing") {
            active.started = true;
            return;
          }

          activeRef.current = null;
          bridge.emit("action", command.unavailableEvent);
        })
        .catch(() => {
          if (activeRef.current === active) {
            activeRef.current = null;
            bridge.emit("action", command.unavailableEvent);
          }
        });
    });

    return () => {
      unsubscribe();
      if (activeRef.current !== null) {
        activeRef.current = null;
        commands.stopVoice();
      }
    };
  }, [bridge, commands]);

  useEffect(() => {
    const active = activeRef.current;
    if (active === null) {
      return;
    }

    if (snapshot.caption?.trackId === active.command.audioKey) {
      bridge.emit("caption", snapshot.caption);
    }

    if (
      !active.started ||
      snapshot.activeVoiceId === active.command.audioKey
    ) {
      return;
    }

    activeRef.current = null;
    const issue = snapshot.lastIssue;
    bridge.emit(
      "action",
      issue?.channel === "voice" &&
        issue.trackId === active.command.audioKey
        ? active.command.unavailableEvent
        : active.command.endedEvent
    );
  }, [bridge, snapshot]);

  return null;
}

function EnglishAutoReady<TAction>({
  bridge
}: EnglishAudioAdapterProps<TAction>): null {
  useLayoutEffect(() => {
    bridge.emit("ready", {});
  }, [bridge]);

  return null;
}

interface EnglishRegisteredActivityProps<
  TKind extends EnglishActivityType,
  TPayload,
  TState,
  TAction,
  TRenderModel
> extends PlayRendererHostProps {
  readonly binding: EnglishPlayerBindingBase<
    TKind,
    TPayload,
    TState,
    TAction,
    TRenderModel
  >;
}

function EnglishRegisteredActivity<
  TKind extends EnglishActivityType,
  TPayload,
  TState,
  TAction,
  TRenderModel
>({
  binding,
  caption,
  nowMs,
  onCommand,
  onComplete,
  onExit,
  onLevelError,
  onReady,
  reducedMotion,
  sessionKey
}: EnglishRegisteredActivityProps<
  TKind,
  TPayload,
  TState,
  TAction,
  TRenderModel
>) {
  const bridge = useMemo<EnglishActivityHostBridge<TAction>>(() => {
    const next = createEventBridge<EnglishActivityHostBridgeEvents<TAction>>();
    next.subscribe("command", onCommand);
    next.subscribe("ready", onReady);
    return next;
  }, [onCommand, onReady]);
  const audioPlayer = useCurriculumAudioPlayer(sessionKey);

  useEffect(() => {
    bridge.emit("caption", caption);
  }, [bridge, caption]);

  const renderActivity = useCallback(
    (context: ActivityHostRenderContext<TAction, TRenderModel>): ReactNode => (
      <>
        <EnglishAutoReady bridge={bridge} />
        <EnglishActivityAudioAdapter bridge={bridge} />
        {binding.renderActivity({ audioPlayer, bridge, context })}
      </>
    ),
    [audioPlayer, binding, bridge]
  );

  return (
    <ActivityHost
      actions={binding.actions}
      adapter={binding.adapter}
      bridge={bridge as EventBridge<ActivityHostBridgeEvents<TAction>>}
      instruction={binding.instruction}
      levelId={binding.level.id}
      levelTitle={binding.level.title}
      nowMs={nowMs}
      onComplete={onComplete}
      onExit={onExit}
      onLevelError={onLevelError}
      payload={binding.level.payload}
      reducedMotion={reducedMotion}
      renderActivity={renderActivity}
    />
  );
}

function requireEnglishLetterSoundBinding(
  levelId: string
): EnglishLetterSoundPlayerBinding {
  const binding = getEnglishPlayerBinding(levelId);
  if (binding === undefined || binding.kind !== "letter-sound") {
    throw new Error("Missing English player binding for " + levelId);
  }
  return binding;
}

function requireEnglishPictureWordBinding(
  levelId: string
): EnglishPictureWordPlayerBinding {
  const binding = getEnglishPlayerBinding(levelId);
  if (binding === undefined || binding.kind !== "picture-word-match") {
    throw new Error("Missing English player binding for " + levelId);
  }
  return binding;
}

function requireEnglishRhythmBinding(
  levelId: string
): EnglishRhythmPlayerBinding {
  const binding = getEnglishPlayerBinding(levelId);
  if (binding === undefined || binding.kind !== "rhythm-phrases") {
    throw new Error("Missing English player binding for " + levelId);
  }
  return binding;
}

function EnglishLetterSoundPlayRenderer(props: PlayRendererHostProps) {
  const binding = requireEnglishLetterSoundBinding(props.level.id);
  return (
    <EnglishRegisteredActivity
      {...props}
      binding={binding}
      key={props.sessionKey}
    />
  );
}

function EnglishPictureWordPlayRenderer(props: PlayRendererHostProps) {
  const binding = requireEnglishPictureWordBinding(props.level.id);
  return (
    <EnglishRegisteredActivity
      {...props}
      binding={binding}
      key={props.sessionKey}
    />
  );
}

function EnglishRhythmPlayRenderer(props: PlayRendererHostProps) {
  const binding = requireEnglishRhythmBinding(props.level.id);
  return (
    <EnglishRegisteredActivity
      {...props}
      binding={binding}
      key={props.sessionKey}
    />
  );
}

interface CurriculumAudioActivityProps {
  readonly children: (player: CurriculumAudioPlayer) => ReactNode;
  readonly ownerKey: string;
}

function CurriculumAudioActivity({
  children,
  ownerKey
}: CurriculumAudioActivityProps) {
  const player = useCurriculumAudioPlayer(ownerKey);
  return children(player);
}

const focusTargetSearchRenderer = definePlayRenderer({
  actions: focusSocialTargetSearchActions,
  adapter: focusSocialTargetSearchAdapter,
  feedbackAudioPolicy: "content",
  renderActivity: ({ context, level }) => (
    <CurriculumAudioActivity ownerKey={level.id}>
      {(audioPlayer) => (
        <FocusSocialTargetSearchRenderer
          {...context}
          audioPlayer={audioPlayer}
        />
      )}
    </CurriculumAudioActivity>
  )
});

const focusFollowDirectionsRenderer = definePlayRenderer({
  actions: focusSocialFollowDirectionsActions,
  adapter: focusSocialFollowDirectionsAdapter,
  feedbackAudioPolicy: "content",
  renderActivity: ({ context, level }) => (
    <CurriculumAudioActivity ownerKey={level.id}>
      {(audioPlayer) => (
        <FocusSocialFollowDirectionsRenderer
          {...context}
          audioPlayer={audioPlayer}
        />
      )}
    </CurriculumAudioActivity>
  )
});

const focusFriendshipStoryRenderer = definePlayRenderer({
  actions: focusSocialFriendshipStoryActions,
  adapter: focusSocialFriendshipStoryAdapter,
  feedbackAudioPolicy: "content",
  renderActivity: ({ context, level }) => (
    <CurriculumAudioActivity ownerKey={level.id}>
      {(audioPlayer) => (
        <FocusSocialFriendshipStoryRenderer
          {...context}
          audioPlayer={audioPlayer}
        />
      )}
    </CurriculumAudioActivity>
  )
});

function registration(
  activityType: AppPlayActivityType,
  Renderer: PlayRendererRegistration["Renderer"],
  feedbackAudioPolicy: PlayRendererRegistration["feedbackAudioPolicy"] =
    "generic"
): PlayRendererRegistration {
  return { activityType, feedbackAudioPolicy, Renderer };
}

export const APP_PLAY_RENDERERS: PlayRendererRegistry = [
  registration("sound-bubbles", ChinesePlayRenderer, "content"),
  registration("picture-character-match", ChinesePlayRenderer, "content"),
  registration("story-sequence", ChinesePlayRenderer, "content"),
  registration("number-train", MathPlayRenderer, "content"),
  registration("forest-market", MathPlayRenderer, "content"),
  registration("pattern-bridge", MathPlayRenderer, "content"),
  registration("letter-sound", EnglishLetterSoundPlayRenderer, "content"),
  registration(
    "picture-word-match",
    EnglishPictureWordPlayRenderer,
    "content"
  ),
  registration("rhythm-phrases", EnglishRhythmPlayRenderer, "content"),
  registration("daily-routine", HabitsPlayRenderer, "content"),
  registration("pack-schoolbag", HabitsPlayRenderer, "content"),
  registration("school-scenarios", HabitsPlayRenderer, "content"),
  focusTargetSearchRenderer,
  focusFollowDirectionsRenderer,
  focusFriendshipStoryRenderer
];

function getBoundActivityType(level: LevelDefinition): string | null {
  switch (level.domain) {
    case "chinese":
      return getChinesePlayerLevel(level.id)?.activityType ?? null;
    case "math":
      return getMathPlayerBinding(level.id)?.activityType ?? null;
    case "english":
      return getEnglishPlayerBinding(level.id)?.kind ?? null;
    case "habits":
      return getHabitsLevelBinding(level.id)?.activityType ?? null;
    case "focus-social":
      return getFocusSocialPlayerBinding(level.id)?.activityType ?? null;
  }
}

const activityTypes = new Set<string>(APP_PLAY_ACTIVITY_TYPES);
const rendererActivityTypes = APP_PLAY_RENDERERS.map(
  ({ activityType }) => activityType
);

if (
  APP_PLAY_RENDERERS.length !== APP_PLAY_ACTIVITY_TYPES.length ||
  new Set(rendererActivityTypes).size !== APP_PLAY_ACTIVITY_TYPES.length ||
  rendererActivityTypes.some((activityType) => !activityTypes.has(activityType))
) {
  throw new Error("The app must register exactly one renderer per activity type.");
}

export const APP_PLAYER_MATRIX: readonly AppPlayerMatrixEntry[] =
  LEVEL_REGISTRY.map((level) => {
    const boundActivityType = getBoundActivityType(level);
    if (
      boundActivityType !== level.activityType ||
      !activityTypes.has(level.activityType)
    ) {
      throw new Error(
        "Missing ID-joined player binding for " +
          level.id +
          " (" +
          level.activityType +
          ")."
      );
    }

    return {
      activityType: level.activityType as AppPlayActivityType,
      domain: level.domain,
      levelId: level.id
    };
  });

if (
  APP_PLAYER_MATRIX.length !== 90 ||
  new Set(APP_PLAYER_MATRIX.map(({ levelId }) => levelId)).size !== 90
) {
  throw new Error("The app player matrix must ID-join all 90 levels exactly once.");
}
